import axios from "axios";
import { OpenAPI } from "@propertelligent/client-api";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatbotChartHint {
  chartType: string; // "bar" | "line" | "pie"
  title?: string | null;
  xKey?: string | null;
  seriesKeys?: string[] | null;
}

export interface ChatbotResponse {
  reply: string;
  isAvailable: boolean;
  error?: string | null;
  confidence?: number | null;
  needsClarification: boolean;
  chart?: ChatbotChartHint | null;
  /** Id of the AiInput log row this answer was persisted as; used to submit a thumbs up/down rating. */
  aiInputId?: number | null;
}

export interface ChatbotTablePayload {
  columns: string[];
  rows: string[][];
}

export interface SendChatbotOptions {
  screenContext?: string;
  signal?: AbortSignal;
}

/**
 * Thin shim over POST /api/Chatbot/message. The generated @propertelligent/client-api does not yet
 * contain a ChatbotService (it is regenerated from the backend Swagger on CI), so this calls the
 * endpoint directly using the same OpenAPI base + token the generated client uses. Mirrors the
 * pattern in login.service.ts. Switch to the generated ChatbotService once it is published.
 *
 * `options.signal` wires an AbortController so the caller can cancel an in-flight ask.
 */
export const sendChatbotMessage = async (
  message: string,
  conversationHistory: ChatMessage[],
  options: SendChatbotOptions = {}
): Promise<ChatbotResponse> => {
  const base = OpenAPI.BASE || "";
  const token = typeof OpenAPI.TOKEN === "string" ? OpenAPI.TOKEN : "";

  const res = await axios.post(
    `${base}/api/Chatbot/message`,
    { message, conversationHistory, screenContext: options.screenContext },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      signal: options.signal,
    }
  );

  const success = res?.data?.success ?? {};
  return {
    reply: success.reply ?? "",
    isAvailable: success.isAvailable ?? true,
    error: success.error ?? null,
    confidence: success.confidence ?? null,
    needsClarification: success.needsClarification ?? false,
    chart: success.chart ?? null,
    aiInputId: success.aiInputId ?? null,
  };
};

/**
 * Server-side export of a single chatbot answer's table to Excel. Calls POST /api/Chatbot/export and
 * returns the file blob. Only valid when a table is present (PDF export was removed — it depended on
 * a headless-browser PDF renderer that was not reliably working in production).
 */
export const exportChatbotResponse = async (
  reportName: string,
  markdownContent: string,
  table: ChatbotTablePayload
): Promise<Blob> => {
  const base = OpenAPI.BASE || "";
  const token = typeof OpenAPI.TOKEN === "string" ? OpenAPI.TOKEN : "";

  const res = await axios.post(
    `${base}/api/Chatbot/export`,
    { format: "excel", reportName, markdownContent, table },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      responseType: "blob",
    }
  );

  return res.data as Blob;
};

/**
 * Submits the caller's thumbs up/down rating for a previously-answered chatbot question. Calls
 * PUT /api/Chatbot/rate. Best-effort from the UI's perspective — callers should swallow failures
 * rather than surface them, since a failed rating shouldn't disrupt the chat experience.
 */
export const rateChatbotResponse = async (aiInputId: number, rating: 1 | 2): Promise<void> => {
  const base = OpenAPI.BASE || "";
  const token = typeof OpenAPI.TOKEN === "string" ? OpenAPI.TOKEN : "";

  await axios.put(
    `${base}/api/Chatbot/rate`,
    { aiInputId, rating },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
};

/** Triggers a browser download for an exported blob. */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
